import moment from "moment"
import { CSSProperties, useState } from "react"
import JSONPretty from "react-json-pretty"
import { useCSVReader } from "react-papaparse"
import { PortHoliday, usePortHolidayAPI } from "../usePortHolidayAPI"
import { Button, Container } from "react-bootstrap"

const styles = {
  csvReader: {
    display: "flex",
    flexDirection: "row",
    marginBottom: 10,
  } as CSSProperties,
  browseFile: {
    width: "20%",
  } as CSSProperties,
  acceptedFile: {
    border: "1px solid #ccc",
    height: 45,
    lineHeight: 2.5,
    paddingLeft: 10,
    width: "80%",
  } as CSSProperties,
  remove: {
    borderRadius: 0,
    padding: "0 20px",
  } as CSSProperties,
  progressBarBackgroundColor: {
    backgroundColor: "red",
  } as CSSProperties,
}

export function PortHolidaysUpload() {
  const { uploadHolidays, deleteAllHolidaysInCache } = usePortHolidayAPI()

  const [error, setError] = useState<string | undefined>()
  const [holidays, setHolidays] = useState<PortHoliday[] | undefined>()
  const [isDeleting, setIsDeleting] = useState(false)

  const { CSVReader } = useCSVReader()
  return (
    <Container className="mt-4">
      <CSVReader
        onUploadAccepted={(results: any) => {
          setError(undefined)
          setHolidays(undefined)
          const parsedArray = results.data

          const holidays = convertToObjects(parsedArray)

          const { isStartDateBeforeEndDate, holiday } =
            getIsStartDateBeforeEndDate(holidays)

          if (!isStartDateBeforeEndDate) {
            setError(`${holiday?.name} has a start date before the end date`)
            return
          }

          setHolidays(holidays)

          const isEntriesAllSameYear = validateSameYear(holidays)
          if (!isEntriesAllSameYear) {
            setError(
              "Please upload a csv with holidays all within the same year"
            )
            return
          }
          uploadHolidays(holidays)
        }}
      >
        {({
          getRootProps,
          acceptedFile,
          ProgressBar,
          getRemoveFileProps,
        }: any) => (
          <>
            <div style={styles.csvReader}>
              <button
                type="button"
                {...getRootProps()}
                style={styles.browseFile}
              >
                Browse file
              </button>
              <div style={styles.acceptedFile}>
                {acceptedFile && acceptedFile.name}
              </div>
              <button
                {...getRemoveFileProps()}
                style={styles.remove}
                onClick={(e) => {
                  setHolidays(undefined)
                  setError(undefined)
                  if (getRemoveFileProps.onClick) {
                    getRemoveFileProps.onClick(e)
                  }
                }}
              >
                Remove
              </button>
            </div>
            <ProgressBar style={styles.progressBarBackgroundColor} />
          </>
        )}
      </CSVReader>
      <h5 style={{ color: "red" }}>{error}</h5>
      <JSONPretty id="json-pretty" data={holidays}></JSONPretty>
      <Button
        onClick={async () => {
          setIsDeleting(true)
          try {
            await deleteAllHolidaysInCache()
            console.log("Holiday cache deleted successfully.")
          } catch (err) {
            console.error("Failed to delete holiday cache:", err)
          } finally {
            setIsDeleting(false)
          }
        }}
        disabled={isDeleting}
      >
        {isDeleting ? "Deleting..." : "Delete Holiday Cache"}
      </Button>
    </Container>
  )
}

type ParsedObject = {
  [key: string]: string
}

function convertToObjects(parsedArray: string[][]) {
  // Extract the headers from the first row
  const headers = parsedArray[0]

  const result = parsedArray
    .slice(1) // Skip the headers
    .filter((row) => row.length > 1)
    .map((row) => {
      let obj: ParsedObject = {}
      headers.forEach((header, index) => {
        obj[header] = row[index] || "" // Assign value or empty string
      })
      return obj
    })
    .map((obj) => {
      return {
        ...obj,
        source: "user",
      }
    })

  return result as PortHoliday[]
}

function validateSameYear(entries: PortHoliday[]) {
  // Extract the year from the first entry's start_date
  const baseYear = moment(entries[0].start_date).year()

  // Check if all start_date and end_date are within the base year
  return entries.every((entry) => {
    const startYear = moment(entry.start_date).year()
    const endYear = moment(entry.end_date).year()
    return startYear === baseYear && endYear === baseYear
  })
}

function getIsStartDateBeforeEndDate(holidays: PortHoliday[]) {
  for (const holiday of holidays) {
    const startDate = moment(holiday.start_date, "YYYY-MM-DD")
    const endDate = moment(holiday.end_date, "YYYY-MM-DD")

    // Check if start_date is not before end_date
    if (!startDate.isBefore(endDate) && !startDate.isSame(endDate)) {
      return { isStartDateBeforeEndDate: false, holiday }
    }
  }
  return { isStartDateBeforeEndDate: true, holiday: undefined }
}
